import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatComponent from './ChatModal';
import { AuthContext } from '../../../context/AuthContext';
import api from '../../../api/axios';

function ConsultationPage({ consultationId, consultationType }) {
  const { token, currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [consultationData, setConsultationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentHour = new Date().getHours();
  const chatAvailable = currentHour >= 9 && currentHour < 22;
  // Video consultations can be booked anytime for future slots
  // The actual slot time validation is handled by consultationService
  const videoAvailable = true;

  // Fetch consultation details
  useEffect(() => {
    if (!token || !consultationId) return;

    setLoading(true);
    api.get(`/consultation/${consultationId}/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      setConsultationData(response.data);
      setError(null);
    })
    .catch(err => {
      setError(err.response?.data?.detail || 'Failed to load consultation details');
    })
    .finally(() => {
      setLoading(false);
    });
  }, [consultationId, token]);

  const getServiceInfo = (type) => {
    if (type === 'chat') {
      return {
        title: 'Medical Chat Consultation',
        description: 'Connect with healthcare professionals through secure text messaging',
        icon: (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        ),
        gradient: 'from-green-500 to-emerald-600',
        available: chatAvailable,
        hours: '9:00 AM - 10:00 PM',
        features: [
          'Instant medical advice',
          'End-to-end encryption',
          'No appointment needed',
          'Free consultation'
        ]
      };
    } else {
      return {
        title: 'Video Call Consultation',
        description: 'Face-to-face consultation with certified medical specialists',
        icon: (
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        ),
        gradient: 'from-blue-500 to-indigo-600',
        available: videoAvailable,
        hours: 'Book anytime - Consultations: 11:00 AM - 5:00 PM',
        features: [
          'HD video consultation',
          'Certified specialists',
          'Prescription available',
          '30-minute sessions'
        ]
      };
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Authentication Required</h2>
          <p className="text-gray-600 mb-6">Please log in to access medical consultation services</p>
          <div className="space-y-2 text-xs text-gray-500">
            <p>✓ HIPAA Compliant</p>
            <p>✓ Secure & Encrypted</p>
            <p>✓ Licensed Healthcare Providers</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading consultation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">Unable to Load Consultation</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const serviceInfo = getServiceInfo(consultationType);

  if (consultationType === 'chat') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className={`bg-gradient-to-r ${serviceInfo.gradient} text-white`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  {serviceInfo.icon}
                </div>
                <div>
                  <h1 className="text-3xl font-bold">{serviceInfo.title}</h1>
                  <p className="text-green-100 mt-2">{serviceInfo.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-green-100">Consultation #{consultationId}</p>
                <p className="text-xs text-green-200">Patient: {currentUser?.name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Service Status */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className={`rounded-xl p-6 mb-6 ${
            serviceInfo.available 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-amber-50 border border-amber-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  serviceInfo.available ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  <svg className={`w-6 h-6 ${
                    serviceInfo.available ? 'text-green-600' : 'text-amber-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d={serviceInfo.available 
                        ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        : "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      }
                    />
                  </svg>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${
                    serviceInfo.available ? 'text-green-800' : 'text-amber-800'
                  }`}>
                    {serviceInfo.available ? 'Medical Chat Available' : 'Medical Chat Unavailable'}
                  </h3>
                  <p className={`text-sm ${
                    serviceInfo.available ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    Service hours: {serviceInfo.hours} daily
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                {serviceInfo.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Chat Interface or Unavailable Message */}
          {serviceInfo.available ? (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="h-[600px]">
                <ChatComponent
                  consultationId={consultationId}
                  currentUserId={currentUser?.id}
                  wsBaseUrl={process.env.REACT_APP_WS_URL}
                />
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Medical Chat Currently Unavailable</h3>
              <div className="max-w-md mx-auto space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 font-semibold">Service Hours</p>
                  <p className="text-amber-700">Monday - Sunday: 9:00 AM - 10:00 PM</p>
                </div>
                <p className="text-gray-600">
                  Our medical chat service will be available during the scheduled hours. 
                  Healthcare professionals are standing by to assist you.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-left">
                      <p className="font-semibold text-red-800">Medical Emergency?</p>
                      <p className="text-red-700 text-sm">Call emergency services immediately</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (consultationType === 'video') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className={`bg-gradient-to-r ${serviceInfo.gradient} text-white`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  {serviceInfo.icon}
                </div>
                <div>
                  <h1 className="text-3xl font-bold">{serviceInfo.title}</h1>
                  <p className="text-blue-100 mt-2">{serviceInfo.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-100">Patient: {currentUser?.name}</p>
                <p className="text-xs text-blue-200">Secure video consultation</p>
              </div>
            </div>
          </div>
        </div>

        {/* Service Status */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className={`rounded-xl p-6 mb-6 ${
            serviceInfo.available 
              ? 'bg-blue-50 border border-blue-200' 
              : 'bg-amber-50 border border-amber-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  serviceInfo.available ? 'bg-blue-100' : 'bg-amber-100'
                }`}>
                  <svg className={`w-6 h-6 ${
                    serviceInfo.available ? 'text-blue-600' : 'text-amber-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d={serviceInfo.available 
                        ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        : "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      }
                    />
                  </svg>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${
                    serviceInfo.available ? 'text-blue-800' : 'text-amber-800'
                  }`}>
                    {serviceInfo.available ? 'Video Consultation Available' : 'Video Consultation Unavailable'}
                  </h3>
                  <p className={`text-sm ${
                    serviceInfo.available ? 'text-blue-600' : 'text-amber-600'
                  }`}>
                    Service hours: {serviceInfo.hours} daily
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                {serviceInfo.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Video Booking Interface or Unavailable Message */}
          {serviceInfo.available ? (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Book Your Video Consultation</h3>
                <p className="text-gray-600 mb-6">Connect with certified medical specialists via secure video call</p>
                
                <button
                  onClick={() => navigate('/video-consultation')}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg inline-flex items-center space-x-3"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Schedule Video Consultation</span>
                </button>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {serviceInfo.features.map((feature, index) => (
                  <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">{feature}</h4>
                    <p className="text-sm text-gray-600">Professional medical care</p>
                  </div>
                ))}
              </div>

              {/* Important Information */}
              <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="font-semibold text-blue-900 mb-4">Important Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Sessions last 30 minutes with certified specialists</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span>Video link will be sent to your WhatsApp 15 minutes before</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.818-4.954A9.959 9.959 0 0121 12c0 5.523-4.477 10-10 10S1 17.523 1 12 5.477 2 11 2a9.959 9.959 0 014.818 1.046" />
                    </svg>
                    <span>Prescriptions can be issued digitally if required</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span>All consultations are secure and HIPAA compliant</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Video Consultations Currently Unavailable</h3>
              <div className="max-w-md mx-auto space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 font-semibold">Service Hours</p>
                  <p className="text-amber-700">Monday - Sunday: 11:00 AM - 5:00 PM</p>
                </div>
                <p className="text-gray-600">
                  Video consultations with our certified medical specialists are available during scheduled hours. 
                  Book your appointment in advance.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <div className="text-left">
                      <p className="font-semibold text-blue-800">Need Immediate Help?</p>
                      <p className="text-blue-700 text-sm">Try our medical chat service (available 9 AM - 10 PM)</p>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-left">
                      <p className="font-semibold text-red-800">Medical Emergency?</p>
                      <p className="text-red-700 text-sm">Call emergency services immediately</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">Invalid Consultation Type</h2>
        <p className="text-gray-600">Please specify a valid consultation type (chat or video)</p>
      </div>
    </div>
  );
}

export default ConsultationPage;